import { useTranslation } from "react-i18next";
import { PageDataStats } from "@/components/PageDataStats";
import { UNBOUND_STAT_TILE, type UseTableParams } from "@/hooks/useTableParams";
import { useGetProjectStatsQuery } from "@/services/(company)/api.projects";
import { ENUM_PROJECT_STATUSES } from "@/types/(company)/projects.interface";
import type { ProjectFilters } from "./projects.params";

interface Props {
  /**
   * The projects table's URL params. Passing them makes each tile a status
   * filter; the project *detail* page shows the same row with no table under
   * it, so it leaves this out and the tiles stay a plain read-out.
   */
  params?: UseTableParams<ProjectFilters>;
}

export const ProjectsStats = ({ params }: Props) => {
  const { t } = useTranslation(["projects", "common"]);
  const { data: { data: stats } = {}, isLoading } = useGetProjectStatsQuery();
  const on = (status: string) =>
    params ? params.statFilter({ status }) : UNBOUND_STAT_TILE;

  const statsData = [
    {
      name: t("projects:total_projects"),
      value: stats?.totalProjects ?? 0,
      icon: "/icons/interface-icons/stats/projects/icon-3d-folder-graph.png",
      ...on(""),
    },
    {
      name: t("projects:completed_projects"),
      value: stats?.completedProjects ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-green-check.png",
      ...on(ENUM_PROJECT_STATUSES.COMPLETED),
    },
    {
      name: t("projects:project_in_progress"),
      value: stats?.inprogressProjects ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-clock-gold.png",
      ...on(ENUM_PROJECT_STATUSES.IN_PROGRESS),
    },
    {
      name: t("projects:projects_on_hold"),
      value: stats?.onholdProjects ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-pause.png",
      ...on(ENUM_PROJECT_STATUSES.ON_HOLD),
    },
  ];

  return <PageDataStats data={statsData} loading={isLoading} />;
};
